import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { User } from '@supabase/supabase-js';
import { AuthContextType, UserProfile, UserRole, DEMO_USERS } from './types';
import { fetchOrCreateUserProfile, getRolePriority } from './profileUtils';
import { logAuthStateChange, logError } from '@/utils/debug';
import { refreshTokenForRoleChanges, getRoleFromJWT, debugAuthState } from '@/utils/jwt-roles';
import { getReliableUserRole } from '@/utils/error-recovery';

// Create context with undefined initial value
export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [supabaseUser, setSupabaseUser] = useState<User | null>(null);
  const [retryCount, setRetryCount] = useState<number>(0);
  const [profileFailure, setProfileFailure] = useState<boolean>(false);
  const navigate = useNavigate();
  const initializationTimer = useRef<NodeJS.Timeout | null>(null);
  const profileLoadAttempts = useRef<number>(0);

  // Track whether this is a new browser tab
  const [isNewTab] = useState(() => {
    // Generate a unique ID for this tab if one doesn't exist
    const existingTabId = sessionStorage.getItem('tabId');
    if (!existingTabId) {
      const newTabId = Math.random().toString(36).substring(2, 15);
      sessionStorage.setItem('tabId', newTabId);
      return true;
    }
    return false;
  });

  // Track data loading completion  
  const [dataInitialized, setDataInitialized] = useState(false);

  // Add timeout protection for initialization
  useEffect(() => {
    // Set a timeout to prevent infinite loading
    if (loading && !dataInitialized) {
      initializationTimer.current = setTimeout(() => {
        console.log('Auth initialization timeout reached (15s), forcing state update');
        
        // If we're still loading after 15 seconds, force a state update based on what we know
        if (loading) {
          // Check if we have a session token but failed to load user data
          const hasToken = localStorage.getItem('supabase.auth.token') !== null;
          
          if (hasToken && !user) {
            console.log('Found auth token but user profile failed to load, forcing session refresh');
            
            // Attempt one last session refresh
            supabase.auth.refreshSession().then(({ data, error }) => {
              if (error) {
                console.error('Final session refresh failed:', error);
                setLoading(false);
                setError(new Error('Authentication initialization timed out. Please try logging in again.'));
              } else if (data.session) {
                console.log('Final session refresh succeeded, setting basic user profile');
                
                // Set basic user profile to unblock the app
                if (!user && data.session.user) {
                  const basicProfile: UserProfile = {
                    id: data.session.user.id,
                    email: data.session.user.email || '',
                    name: data.session.user.user_metadata?.name || data.session.user.email?.split('@')[0] || 'User',
                    role: 'user',
                    organisationId: 'org-001',
                    restaurants: []
                  };
                  setUser(basicProfile);
                  // Mark profile as initialized, even if it was a fallback
                  setDataInitialized(true);
                  // Note that this was a fallback profile
                  setProfileFailure(true);
                }
                
                // Force loading state to finish
                setLoading(false);
              }
            });
          } else {
            // No token or we already have user data, just finish loading
            setLoading(false);
          }
        }
      }, 15000); // 15 second timeout
    }
    
    return () => {
      if (initializationTimer.current) {
        clearTimeout(initializationTimer.current);
      }
    };
  }, [loading, dataInitialized, user]);

  // Add a specific handler for profile loading errors that attempts recovery
  const handleProfileLoadError = async (user: User, error: any): Promise<UserProfile | null> => {
    console.error('Error in profile loading:', error);
    
    try {
      // Try a direct insert with explicit schema as a recovery attempt
      console.log('Attempting recovery with direct profile creation');
      const { data, error: insertError } = await supabase
        .from('public.profiles')  // Use explicit schema reference
        .insert([{ 
          id: user.id, 
          email: user.email || '',
          name: user.user_metadata?.name || user.user_metadata?.full_name || '',
          role: UserRole.USER
        }])
        .select()
        .single();
        
      if (insertError) {
        console.error('Recovery profile creation failed:', insertError);
        return null;
      }
      
      if (data) {
        console.log('Recovery profile creation succeeded:', data.id);
        return {
          id: data.id,
          email: data.email || '',
          name: data.name || '',
          role: UserRole.USER,
          organisationId: data.organisation_id || 'org-001',
          restaurants: []
        };
      }
    } catch (recoveryError) {
      console.error('Profile recovery attempt failed:', recoveryError);
    }
    
    return null;
  };

  // Create a specific effect for loading user profile data
  useEffect(() => {
    // If we have a Supabase user but no profile or data initialization
    if (supabaseUser && (!user || !dataInitialized) && !loading) {
      const loadUserProfile = async () => {
        try {
          // Increment attempt counter
          profileLoadAttempts.current += 1;
          console.log(`Attempting to load user profile (attempt ${profileLoadAttempts.current})`);
          
          // Set loading state for profile loading
          setLoading(true);
          
          // Try to get role from JWT first for better resilience
          const jwtRole = await getRoleFromJWT();
          console.log('JWT role extraction result:', jwtRole);
          
          const userProfile = await fetchOrCreateUserProfile(supabaseUser);
          
          // Emergency role recovery if profile loading failed multiple times
          if (!userProfile && profileLoadAttempts.current >= 2) {
            console.log('Multiple profile load failures, attempting emergency role recovery');
            
            // Get the most reliable role available
            const recoveredRole = await getReliableUserRole(supabaseUser.id);
            
            // Create emergency profile with recovered role
            const emergencyProfile: UserProfile = {
              id: supabaseUser.id,
              email: supabaseUser.email || '',
              name: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'User',
              role: recoveredRole, // Use the recovered role instead of defaults
              organisationId: 'org-001',
              restaurants: []
            };
            
            console.log('Created emergency profile with role:', recoveredRole);
            setUser(emergencyProfile);
            setDataInitialized(true);
            setProfileFailure(true); // Mark that this was a recovery profile
            setLoading(false);
            return; // Exit early after handling recovery
          }
          
          if (userProfile) {
            console.log('User profile loaded successfully:', userProfile.id);
            
            // Set user profile 
            setUser(userProfile);
            setDataInitialized(true);
            setProfileFailure(false); // Reset failure flag on success
            setLoading(false);
          } else {
            throw new Error('Failed to fetch user profile data');
          }
        } catch (err) {
          console.error('Error loading user profile:', err);
          
          // Try a direct recovery method
          if (profileLoadAttempts.current < 3) {
            const recoveryProfile = await handleProfileLoadError(supabaseUser, err);
            if (recoveryProfile) {
              setUser(recoveryProfile);
              setDataInitialized(true);
              setProfileFailure(false);
              setLoading(false);
              return;
            }
          }
          
          // If we've tried too many times or recovery failed, use a basic profile
          if (profileLoadAttempts.current >= 3) {
            console.log('Creating fallback user profile after failed attempts');
            
            // Try to get role from JWT as a priority for fallback profile
            const jwtRole = await getRoleFromJWT();
            
            // Create a basic profile from the Supabase user object using the enum for role
            const basicProfile: UserProfile = {
              id: supabaseUser.id,
              email: supabaseUser.email || '',
              name: supabaseUser.user_metadata?.name || supabaseUser.email?.split('@')[0] || 'User',
              role: jwtRole || UserRole.USER, // Prioritize JWT role if available
              organisationId: 'org-001',
              restaurants: []
            };
            
            setUser(basicProfile);
            setDataInitialized(true);
            setProfileFailure(true); // Mark that we're using a fallback profile
          }
          
          setLoading(false);
        }
      };
      
      loadUserProfile();
    }
  }, [supabaseUser, user, dataInitialized, loading]);

  // Check if user is already logged in with Supabase
  useEffect(() => {
    setLoading(true);
    setDataInitialized(false);
    
    // Log which tab is initializing
    console.log(`Tab ${sessionStorage.getItem('tabId')} initializing auth state`);
    
    // Error handler for auth operations with improved logging
    const handleAuthError = (operation: string, error: any) => {
      logError(`Auth operation: ${operation}`, error);
      setError(error instanceof Error ? error : new Error(String(error)));
      return false; // Could not recover
    };
    
    // Enhanced session check with more robust recovery
    const checkSupabaseSession = async () => {
      console.log("📍 Starting session check...");
      
      try {
        logAuthStateChange('Checking session', { loading: true });
        
        // First, try to get the current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        console.log("📍 Session check result:", {
          hasSession: !!session,
          userId: session?.user?.id,
          tokenExpiry: session?.expires_at ? new Date(session.expires_at * 1000).toISOString() : null,
          error: sessionError?.message
        });
        
        // If no session but we're expecting one (e.g., when reopening a tab)
        // Try to refresh the session more aggressively
        if (!session && localStorage.getItem('supabase.auth.token')) {
          logAuthStateChange('Attempting to refresh expired session', {});
          
          try {
            const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
            
            if (refreshError) {
              throw refreshError;
            }
            
            // If we successfully refreshed, use that session
            if (refreshData.session) {
              logAuthStateChange('Session refresh successful', { 
                userId: refreshData.session.user.id 
              });
              
              // Continue with the refreshed session
              const refreshedSession = refreshData.session;
              
              setSupabaseUser(refreshedSession.user);
              // Note: We don't try to fetch the profile here, we'll let the dedicated effect handle that
              
              // Reset error and retry count
              setError(null);
              setRetryCount(0);
              setLoading(false);
              return; // Exit early as we've handled everything
            }
          } catch (refreshError) {
            logError('Session refresh attempt failed', refreshError);
            // Continue to normal flow, don't exit
          }
        }
        
        if (session?.user) {
          logAuthStateChange('User is logged in', { 
            isAuthenticated: true, 
            userId: session.user.id 
          });
          
          setSupabaseUser(session.user);
          // Note: We don't try to fetch the profile here, we'll let the dedicated effect handle that
          
          // We've done our part, now let the profile effect take over
          setLoading(false);
        } else {
          // No active session or stored user
          setUser(null);
          setSupabaseUser(null);
          logAuthStateChange('No active session', { isAuthenticated: false });
          setLoading(false);
        }
        
        // Reset error and retry count on success
        setError(null);
        setRetryCount(0);
      } catch (err) {
        logError('Session check error', err);
        setError(err instanceof Error ? err : new Error(String(err)));
        
        // Attempt recovery with local storage
        const recovered = handleAuthError('checkSession', err);
        
        // If not recovered and under retry limit, schedule a retry
        if (!recovered && retryCount < 3) {
          const nextRetry = retryCount + 1;
          setRetryCount(nextRetry);
          
          // Exponential backoff for retries
          const delay = Math.pow(2, nextRetry) * 1000;
          
          setTimeout(() => {
            logAuthStateChange('Retrying session check', { 
              attempt: nextRetry, 
              maxAttempts: 3 
            });
            checkSupabaseSession();
          }, delay);
          
          return; // Don't set loading to false yet
        } else {
          // We've exhausted retries or recovery succeeded
          setLoading(false);
        }
      }
    };
    
    // Listen for auth changes with enhanced error handling
    let subscription: { unsubscribe: () => void } | null = null;
    
    try {
      const authStateSubscription = supabase.auth.onAuthStateChange(
        async (event, session) => {
          logAuthStateChange(event, { 
            hasSession: !!session, 
            userId: session?.user?.id 
          });
          
          if (event === 'SIGNED_IN') {
            console.log("Auth state changed SIGNED_IN", session?.user?.id);
            if (session?.user) {
              setSupabaseUser(session.user);
              // Note: We don't fetch the profile here - dedicated effect will handle it
              
              // Let the profile loading effect take over
              setLoading(false);
            }
          } else if (event === 'SIGNED_OUT') {
            setSupabaseUser(null);
            setUser(null);
            setDataInitialized(false);
            setProfileFailure(false);
            profileLoadAttempts.current = 0;
            
            // Centralized navigation for logout
            logAuthStateChange('Navigation on sign out', { 
              from: window.location.pathname,
              to: '/auth'
            });
            navigate('/auth');
          } else if (event === 'USER_UPDATED') {
            // Handle user updates - refresh profile data
            if (session?.user) {
              setSupabaseUser(session.user);
              // Reset profile attempts counter to allow a fresh try
              profileLoadAttempts.current = 0;
            }
          }
        }
      );
      
      subscription = authStateSubscription.data.subscription;
    } catch (subscriptionError) {
      logError('Auth subscription error', subscriptionError);
      // Continue with session check even if subscription fails
    }
    
    // Set up cross-tab communication
    const handleStorageChange = (event: StorageEvent) => {
      // If another tab signals a logout
      if (event.key === 'auth_logout_signal' && event.newValue === null) {
        console.log('Logout detected in another tab');
        // Only if we're authenticated, sign out here too
        if (user !== null) {
          setUser(null);
          setSupabaseUser(null);
          setDataInitialized(false);
          setProfileFailure(false);
          profileLoadAttempts.current = 0;
          navigate('/auth');
        }
      }
      // If another tab signs in or out via Supabase
      else if (event.key === 'supabase.auth.token') {
        console.log('Auth token changed in another tab, refreshing auth state');
        checkSupabaseSession();
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    checkSupabaseSession();
    
    return () => {
      if (subscription) {
        subscription.unsubscribe();
      }
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [navigate, retryCount]);

  // Add refreshSession method
  const refreshSession = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.refreshSession();
      if (error) throw error;
      
      if (data.session?.user) {
        setSupabaseUser(data.session.user);
        profileLoadAttempts.current = 0;
      }
      
      return data.session;
    } catch (error) {
      console.error('Error refreshing session:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    setError(null);
    
    try {
      logAuthStateChange('Login attempt', { email });
      
      // Try Supabase login first
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        logAuthStateChange('Supabase login failed', { 
          errorMessage: error.message 
        });
        console.warn('Supabase login failed, falling back to demo users:', error.message);
        
        // Fall back to demo users for development convenience
        // This should be removed in production
        if (process.env.NODE_ENV === 'development') {
          const foundUser = DEMO_USERS.find(u => u.email === email && u.password === password);
          
          if (!foundUser) {
            throw new Error('Invalid credentials');
          }
          
          // Create user object without password
          const { password: _, ...userWithoutPassword } = foundUser;
          
          // Save to state (but not localStorage for security)
          setUser(userWithoutPassword);
          
          logAuthStateChange('Demo user login successful', { 
            userId: userWithoutPassword.id, 
            name: userWithoutPassword.name,
            role: userWithoutPassword.role // Now using the enum value
          });
          
          toast({
            title: "Login successful",
            description: `Welcome back, ${userWithoutPassword.name}!`,
          });
          
          navigate('/dashboard');
        } else {
          // In production, don't use demo users
          throw new Error('Invalid credentials');
        }
      } else if (data.user) {
        logAuthStateChange('Supabase login successful', { 
          userId: data.user.id 
        });
        
        // Add this line to refresh token and get updated role claims
        await refreshTokenForRoleChanges();
        
        toast({
          title: "Login successful",
          description: "Welcome back!",
        });
        // Auth state change listener will handle redirects
      }
    } catch (error) {
      logError('Login error', error);
      
      setError(error instanceof Error ? error : new Error(String(error)));
      
      toast({
        variant: "destructive",
        title: "Login failed",
        description: error instanceof Error ? error.message : "An unknown error occurred",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signup = async (email: string, password: string, name: string) => {
    setLoading(true);
    setError(null);
    
    try {
      logAuthStateChange('Signup attempt', { email, name });
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name
          },
          emailRedirectTo: `${window.location.origin}/auth/reset-password`
        }
      });
      
      if (error) {
        throw error;
      }
      
      // Try to explicitly create a profile after signup
      if (data.user) {
        try {
          console.log('Attempting to create profile after signup');
          const { error: profileError } = await supabase
            .from('public.profiles')  // Use explicit schema reference
            .insert([{
              id: data.user.id,
              email: data.user.email || '',
              name: name || '',
              role: 'user'
            }]);
            
          if (profileError) {
            console.error('Manual profile creation after signup failed:', profileError);
            // Continue anyway - the trigger might handle it
          } else {
            console.log('Profile created successfully after signup');
          }
        } catch (profileError) {
          console.error('Error in manual profile creation after signup:', profileError);
          // Continue with signup process
        }
      }
      
      logAuthStateChange('Signup successful', { 
        email, 
        userId: data.user?.id 
      });
      
      toast({
        title: "Sign up successful",
        description: "Please check your email to confirm your account.",
      });
      
      // Switch to login tab
      navigate('/auth');
    } catch (error: any) {
      logError('Signup error', error);
      
      setError(error instanceof Error ? error : new Error(String(error)));
      
      toast({
        variant: "destructive",
        title: "Sign up failed",
        description: error.message || "An unknown error occurred",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    setError(null);
    
    try {
      logAuthStateChange('Google signin attempt', {});
      
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`,
        }
      });
      
      if (error) {
        throw error;
      }
      
      // The auth state will be handled by the auth state change listener
    } catch (error: any) {
      logError('Google login error', error);
      
      setError(error instanceof Error ? error : new Error(String(error)));
      
      toast({
        variant: "destructive",
        title: "Google login failed",
        description: error.message || "An error occurred during Google login",
      });
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      logAuthStateChange('Logout attempt', {
        hasSupabaseUser: !!supabaseUser,
        hasUser: !!user
      });
      
      // Signal to other tabs that we're logging out (before the actual signOut)
      // This helps with coordination in multi-tab scenarios
      try {
        sessionStorage.setItem('manual_logout', 'true');
        localStorage.setItem('auth_logout_signal', Date.now().toString());
        // Remove immediately to trigger storage events in other tabs
        localStorage.removeItem('auth_logout_signal');
      } catch (e) {
        console.warn('Failed to signal logout to other tabs:', e);
      }
      
      // Sign out from Supabase
      if (supabaseUser) {
        await supabase.auth.signOut();
      }
      
      // Clear state
      setUser(null);
      setSupabaseUser(null);
      setError(null);
      
      // Redirect to auth directly instead of going through Index
      navigate('/auth');
      
      logAuthStateChange('Logout successful', {});
      
      toast({
        title: "Logged out",
        description: "You have been successfully logged out.",
      });
    } catch (error) {
      logError('Logout error', error);
      
      // Still clear local state even if Supabase logout fails
      setUser(null);
      setSupabaseUser(null);
      
      // Redirect
      navigate('/auth');
      
      toast({
        title: "Logged out",
        description: "You have been logged out with some errors.",
      });
    }
  };

  // Add debugging function
  const debugAuthStateCallback = useCallback(() => {
    debugAuthState();
    
    console.group("===== AUTH PROVIDER DEBUG =====");
    console.log("Current auth state:", {
      user: user ? {
        id: user.id,
        email: user.email ? '[REDACTED EMAIL]' : null,
        role: user.role,
        hasRestaurants: user.restaurants?.length > 0
      } : null,
      loading,
      isAuthenticated: !!user,
      dataInitialized,
      profileFailure,
      retryCount,
      hasSupabaseUser: !!supabaseUser,
      profileLoadAttempts: profileLoadAttempts.current
    });
    
    // Check localStorage state
    try {
      const storageKey = 'supabase.auth.token';
      const hasStoredAuth = !!localStorage.getItem(storageKey);
      
      // Look for alternative keys
      const supabaseKeys = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.includes('supabase')) {
          supabaseKeys.push(key);
        }
      }
      
      console.log("Auth storage:", {
        hasToken: hasStoredAuth,
        supabaseRelatedKeys: supabaseKeys
      });
    } catch (e) {
      console.error("Storage check error:", e);
    }
    
    console.groupEnd();
  }, [user, loading, dataInitialized, profileFailure, retryCount, supabaseUser]);

  // Add debug effect
  useEffect(() => {
    const timer = setTimeout(() => {
      debugAuthStateCallback();
    }, 2000); // 2 seconds after mount
    
    return () => clearTimeout(timer);
  }, [debugAuthStateCallback]);

  // Ensure data is fully loaded before removing loading state
  useEffect(() => {
    // If authenticated but data not initialized, wait for user profile
    if (!!user && !dataInitialized) {
      console.log('User authenticated, waiting for data initialization');
      // Set a small delay to ensure dependent contexts can initialize
      const timer = setTimeout(() => {
        console.log('Data initialization complete');
        setDataInitialized(true);
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [user, dataInitialized]);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading: loading || (!!supabaseUser && !dataInitialized), // Keep loading true until data is initialized
        error, // Now exposing error state to consumers
        login,
        signup,
        signInWithGoogle,
        logout,
        isAuthenticated: !!user,
        dataInitialized,
        profileFailure, // Expose whether we're using a fallback profile
        refreshSession // Add the refreshSession method to the context
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
